import {
  Box,
  Button,
  Grid,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useGetStoreQuery } from 'api/client-domain/client-domain';
import { useGetOrderDetailsQuery } from 'api/ordering-service/ordering-service';
import { SalesReportOrder } from 'api/ordering-service/ordering-service.types';
import { useGetCustomerQuery } from 'api/user-domain/user-domain';
import { PAYMENT_TYPES } from 'appConstants';
import CustomerCard from 'components/customer/customer-card/customer-card.component';
import { DateTime } from 'luxon';
import OrderInformation from 'pages/analytics/salesreport/order-details-info.component';
import OrderSummaryInformation from 'pages/analytics/salesreport/order-summary-info.component';
import { useSendReceipt } from 'pages/payments/transactions/transactions-data-grid/useSendReceipt.hooks';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import IncDrawer, { IncDrawerType } from '../inc-drawer/inc-drawer.component';
import OrderRefundDrawer from '../order-refund-drawer/order-refund-drawer.component';
import UtilityDrawerBody from '../utility-drawer/utility-drawer-body/utility-drawer-body.component';
import { useUtilityDrawer } from '../utility-drawer/utility-drawer.hooks';
import { ArrowForward } from '@mui/icons-material';
import CustomerLink from 'components/customer/customer-name/customer-link.component';
import { getCustomerFullName } from 'components/customer/customer.utils';
import { getCCNumberGCNumberText } from 'util/payment-types/pament-types';

export type OrderDetailsDrawerDataType = Partial<SalesReportOrder>;

type OrderDetailsDetailsDrawerProps = IncDrawerType<OrderDetailsDrawerDataType>;

const OrderDetailsDrawer = ({
  close,
  data,
  isOpen,
  ...drawerRest
}: OrderDetailsDetailsDrawerProps) => {
  const { t } = useTranslation();
  const {
    currentData: orderDetails,
    isFetching: getorderDetailsLoading,
    refetch: refetchOrderDetails,
  } = useGetOrderDetailsQuery(data?.orderId ?? skipToken);

  useEffect(() => {
    if (isOpen && data?.orderId) {
      refetchOrderDetails();
    }
  }, [isOpen, data?.orderId, refetchOrderDetails]);

  const { currentData: storeDetails } = useGetStoreQuery(
    orderDetails?.locationId ?? skipToken,
  );

  const customerId =
    orderDetails?.customer?.customerId === '0'
      ? null
      : orderDetails?.customer?.customerId;

  const { currentData: customerData } = useGetCustomerQuery(
    customerId ?? skipToken,
  );

  const refundDetailsUtilityDrawer = useUtilityDrawer();

  const handleRefundDetailsDrawerOpen = () => {
    refundDetailsUtilityDrawer.setDrawerData({ orderDetails });
    refundDetailsUtilityDrawer.setOpen(true);
  };

  const sendReceiptprops = {
    orderId: orderDetails?.orderId,
    type: PAYMENT_TYPES.ORDER,
    customer: {
      email: orderDetails?.customer?.email,
    },
  };

  const { trigger: triggerSendReceipt } = useSendReceipt(sendReceiptprops);

  const checkPaymentMethod = paymentType => {
    switch (paymentType) {
      case 'CARD_NOT_PRESENT':
        return 'Credit Card';
      case 'GIFT_CARD':
        return 'Gift Card';
      case 'APPLE_PAY':
        return 'Apple Pay';
      case 'GOOGLE_PAY':
        return 'Google Pay';
      default:
        return 'Other Payment';
    }
  };

  const customerInfo =
    orderDetails?.customer?.customerId === '0'
      ? orderDetails?.customer
      : customerData;

  return (
    <>
      <IncDrawer
        {...drawerRest}
        isOpen={isOpen}
        close={close}
        data={data}
        data-testid="OrderDetails.Drawer"
        title={t('drawer.orderDetailsDrawer.title')}
        isLoading={getorderDetailsLoading}
      >
        <UtilityDrawerBody>
          <Box display={'flex'} justifyContent={'flex-end'} mb={2}>
            <MuiLink
              sx={{ display: 'flex', alignItems: 'flex-end' }}
              component={Link}
              to={`/order/${data?.orderId}`}
              onClick={() => {
                close();
              }}
            >
              <Typography variant="body2">
                {t('drawer.orderDetailsDrawer.viewFullOrder')}
              </Typography>
              <ArrowForward sx={{ mr: 1, ml: 1 }} fontSize="small" />
            </MuiLink>
          </Box>
          {customerInfo && <CustomerCard customer={customerInfo} />}
          <Box>
            <Typography variant="h2" gutterBottom mb={3} mt={4}>
              {t('drawer.orderDetailsDrawer.purchaseInfo')}
            </Typography>
            <Box mb={2}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: '700' }}
              >
                {t('drawer.orderDetailsDrawer.customer')}
              </Typography>
              {customerId ? (
                <CustomerLink
                  customerName={getCustomerFullName(customerInfo)}
                  customerId={customerId}
                />
              ) : (
                <Typography variant="subtitle2" gutterBottom>
                  {getCustomerFullName(customerInfo)}
                </Typography>
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: '700' }}
                >
                  {t('drawer.orderDetailsDrawer.store')}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  {storeDetails?.title}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: '700' }}
                >
                  {t('drawer.orderDetailsDrawer.source')}
                </Typography>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  data-testid={'OrderDetailsDrawer.orderSource'}
                >
                  {orderDetails?.orderSource ? orderDetails?.orderSource : '-'}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={0}>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: '700' }}
                >
                  {t('drawer.orderDetailsDrawer.orderType')}
                </Typography>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  data-testid={'OrderDetailsDrawer.orderType'}
                >
                  {orderDetails?.orderType ? orderDetails?.orderType : '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: '700' }}
                >
                  {t('drawer.orderDetailsDrawer.paymentMethod')}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  {checkPaymentMethod(
                    orderDetails?.checks?.[0]?.payments?.[0]?.paymentType,
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={0}>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: '700' }}
                >
                  {getCCNumberGCNumberText(
                    orderDetails?.checks?.[0]?.payments?.[0]?.paymentType,
                  )}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  {orderDetails?.checks?.[0]?.payments?.[0]?.lastFour
                    ? t('drawer.orderDetailsDrawer.endingIn', {
                        ccNumber:
                          orderDetails?.checks?.[0]?.payments?.[0]?.lastFour,
                      })
                    : '-'}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={0}>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: '700' }}
                >
                  {t('drawer.orderDetailsDrawer.type')}
                </Typography>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  data-testid={'OrderDetailsDrawer.cardType'}
                >
                  {orderDetails?.checks?.[0]?.payments?.[0]?.cardType
                    ? orderDetails?.checks?.[0]?.payments?.[0]?.cardType
                    : '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: '700' }}
                >
                  {t('drawer.orderDetailsDrawer.owner')}
                </Typography>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  data-testid={'OrderDetailsDrawer.firstName'}
                >
                  {orderDetails?.checks?.[0]?.customer?.firstName
                    ? orderDetails?.checks?.[0]?.customer?.firstName
                    : '-'}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={0}>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: '700' }}
                >
                  {orderDetails?.orderType === 'DELIVERY'
                    ? t('drawer.orderDetailsDrawer.deliveryOptions')
                    : t('drawer.orderDetailsDrawer.pickupOptions')}
                </Typography>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  data-testid={'OrderDetailsDrawer.orderOptionName'}
                >
                  {orderDetails?.orderOptionName
                    ? orderDetails?.orderOptionName
                    : '-'}
                </Typography>
              </Grid>
              {orderDetails?.orderType === 'DELIVERY' && (
                <Grid size={{ xs: 6 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: '700' }}
                  >
                    {t('drawer.orderDetailsDrawer.deliveryAddress')}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    data-testid={'OrderDetailsDrawer.deliveryAddress'}
                  >
                    {`${orderDetails?.delivery?.deliveryAddress?.street1} ${orderDetails?.delivery?.deliveryAddress?.street2} ${orderDetails?.delivery?.deliveryAddress?.city} ${orderDetails?.delivery?.deliveryAddress?.country} ${orderDetails?.delivery?.deliveryAddress?.state}`}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Grid container spacing={2} mt={0}>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: '700' }}
                >
                  {t('drawer.orderDetailsDrawer.deliveryInstructions')}
                </Typography>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  data-testid={'OrderDetailsDrawer.deliveryInstructions'}
                >
                  {orderDetails?.delivery?.deliveryInstructions
                    ? orderDetails?.delivery?.deliveryInstructions
                    : '-'}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={0}>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: '700' }}
                >
                  {t('drawer.orderDetailsDrawer.orderNumber')}
                </Typography>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  data-testid={'OrderDetailsDrawer.orderNumber'}
                >
                  {orderDetails?.orderNumber ? orderDetails?.orderNumber : '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: '700' }}
                >
                  {t('drawer.orderDetailsDrawer.orderId')}
                </Typography>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  data-testid={'OrderDetailsDrawer.orderId'}
                >
                  {orderDetails?.orderId ? orderDetails?.orderId : '-'}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={0}>
              <Grid>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: '700' }}
                >
                  {t('drawer.orderDetailsDrawer.orderExtId')}
                </Typography>
                <Typography
                  variant="subtitle2"
                  data-testid={'OrderDetailsDrawer.extOrderId'}
                >
                  {orderDetails?.extOrderId ? orderDetails?.extOrderId : '-'}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={0}>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: '700' }}
                >
                  {t('drawer.orderDetailsDrawer.requested')}
                </Typography>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  data-testid={'OrderDetailsDrawer.orderFulFillTime'}
                >
                  {orderDetails?.orderFulfillTime
                    ? DateTime.fromISO(
                        orderDetails?.orderFulfillTime,
                      ).toLocaleString(DateTime.DATETIME_MED)
                    : '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: '700' }}
                >
                  {t('drawer.orderDetailsDrawer.created')}
                </Typography>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  data-testid={'OrderDetailsDrawer.createdDate'}
                >
                  {orderDetails?.createdDate
                    ? DateTime.fromISO(
                        orderDetails?.createdDate,
                      ).toLocaleString(DateTime.DATETIME_MED)
                    : '-'}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={0}>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: '700' }}
                >
                  {t('drawer.orderDetailsDrawer.closed')}
                </Typography>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  data-testid={'OrderDetailsDrawer.closedTime'}
                >
                  {orderDetails?.closedTime
                    ? DateTime.fromISO(orderDetails?.closedTime).toLocaleString(
                        DateTime.DATETIME_MED,
                      )
                    : '-'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <OrderInformation
            orderDetails={orderDetails}
            orderInfoHeader={
              <Typography variant="h2" gutterBottom mb={3} mt={4}>
                {t('drawer.orderDetailsDrawer.orderInformation')}
              </Typography>
            }
          />

          <OrderSummaryInformation
            orderDetails={orderDetails}
            customGridColumns={12}
          />

          {orderDetails?.status === 'CLOSED' && (
            <Stack spacing={1} mt={12}>
              <Button
                data-testid="orderDetailsDrawer.refundPayment"
                variant="contained"
                onClick={handleRefundDetailsDrawerOpen}
              >
                {t('drawer.orderDetailsDrawer.refundPayment')}
              </Button>
              <Button variant="outlined" onClick={triggerSendReceipt}>
                {t('drawer.orderDetailsDrawer.sendReceipt')}
              </Button>
            </Stack>
          )}
        </UtilityDrawerBody>
      </IncDrawer>
      <OrderRefundDrawer {...refundDetailsUtilityDrawer} />
    </>
  );
};

export default OrderDetailsDrawer;
